// pages/sop/SopFolderDetails.jsx
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Skeleton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// ─── Design tokens (matched to FoldersList / SopFoldersList) ───────────────
const TEAL = "#0c8a9b";
const TEAL_DARK = "#0a7a8a";
const TEAL_LIGHT = "#e6f6f8";
const FONT = "'DM Sans', 'Inter', sans-serif";

const KF_ID = "sop-folder-details-kf";
if (typeof document !== "undefined" && !document.getElementById(KF_ID)) {
  const s = document.createElement("style");
  s.id = KF_ID;
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(s);
}

const statusStyles = {
  DRAFT: { bg: "#f1f3f5", color: "#64748b" },
  PUBLISHED: { bg: TEAL_LIGHT, color: TEAL },
  ARCHIVED: { bg: "#fef3e2", color: "#e07b00" },
};

const SkeletonCard = () => (
  <Card
    sx={{
      borderRadius: "16px",
      border: "1px solid #edf0f2",
      boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    }}
  >
    <CardContent sx={{ p: 2.3 }}>
      <Skeleton
        variant="rounded"
        width={40}
        height={40}
        sx={{ borderRadius: "10px", mb: 1.5 }}
      />
      <Skeleton variant="text" width="65%" height={22} />
      <Skeleton variant="text" width="85%" height={16} sx={{ mt: 0.5 }} />
      <Skeleton
        variant="rounded"
        width={70}
        height={22}
        sx={{ borderRadius: "6px", mt: 1.5 }}
      />
    </CardContent>
  </Card>
);

const SopFolderDetails = () => {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useContext(AutoTaskAuthContext);
  const store_id = userInfo?.store_id;
  const [sops, setSops] = useState([]);
  const [folder, setFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Get folder name
        const foldersRes = await axios.get(
          `${API_BASE}/api/sop-manager/sop-folders`,
          {
            params: { store_id },
          },
        );
        const currentFolder = foldersRes.data.data.find(
          (f) => f.id === parseInt(folderId),
        );
        setFolder(currentFolder);
        // Get SOPs in folder
        const sopsRes = await axios.get(
          `${API_BASE}/api/sop-manager/sops/folder/${folderId}`,
          { params: { store_id } },
        );
        setSops(sopsRes.data.data || []);
        setError("");
      } catch (err) {
        console.error(err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [folderId, store_id]);

  const handleDelete = async (sopId) => {
    if (!window.confirm("Delete this SOP?")) return;
    try {
      await axios.delete(`${API_BASE}/api/sop-manager/sops/${sopId}`);
      setSops(sops.filter((s) => s.id !== sopId));
    } catch (err) {
      alert("Failed to delete SOP");
    }
  };

  if (error)
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: "12px", fontFamily: FONT }}>
          {error}
        </Alert>
      </Box>
    );

  if (!loading && !folder)
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        <Alert
          severity="warning"
          sx={{ borderRadius: "12px", fontFamily: FONT }}
        >
          Folder not found
        </Alert>
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "80vh",
        background: "#f8f9fa",
        fontFamily: FONT,
        p: { xs: 2, sm: 3.5 },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3.5,
          gap: 2,
          animation: "fadeUp .4s ease both",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Tooltip title="Back to folders">
            <IconButton
              onClick={() => navigate("/ats/sop")}
              sx={{
                bgcolor: "#fff",
                border: "1px solid #edf0f2",
                borderRadius: "10px",
                mt: 0.3,
                flexShrink: 0,
                "&:hover": { borderColor: TEAL, color: TEAL },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontFamily: FONT,
                  color: "#1a1d23",
                  fontSize: "1.5rem",
                  letterSpacing: "-0.3px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {loading ? <Skeleton width={180} /> : folder?.name}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#868e96",
                mt: 0.4,

                fontFamily: FONT,
                fontSize: "0.875rem",
              }}
            >
              {loading ? (
                <Skeleton width={120} />
              ) : (
                `${sops.length} SOP${
                  sops.length === 1 ? "" : "s"
                } in this folder`
              )}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => navigate(`/ats/sop/folder/${folderId}/new`)}
          sx={{
            background: TEAL,
            color: "#fff",
            borderRadius: "10px",
            px: 2,
            py: 0.9,
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: "0.85rem",
            textTransform: "none",
            boxShadow: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": { background: TEAL_DARK, boxShadow: "none" },
          }}
        >
          New SOP
        </Button>
      </Box>

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {loading && (
        <Grid container spacing={2.5}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Empty state ───────────────────────────────────────────────── */}
      {!loading && sops.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            animation: "fadeUp .5s ease both",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: TEAL_LIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 34, color: TEAL }} />
          </Box>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontFamily: FONT, color: "#1a1d23" }}
          >
            No SOPs in this folder
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#868e96", mt: 0.5, fontFamily: FONT }}
          >
            Create your first standard operating procedure here.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/ats/sop/folder/${folderId}/new`)}
            sx={{
              mt: 2.5,
              background: TEAL,
              borderRadius: "10px",
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            Create SOP
          </Button>
        </Box>
      )}

      {/* ── SOP Cards ─────────────────────────────────────────────────── */}
      {!loading && sops.length > 0 && (
        <Grid container spacing={2.2}>
          {sops.map((sop, idx) => {
            const delay = `${idx * 0.05}s`;
            const st = statusStyles[sop.status] || statusStyles.DRAFT;

            return (
              <Grid item xs={12} sm={6} md={3} key={sop.id}>
                <Card
                  onClick={() => navigate(`/ats/sop/view/${sop.id}`)}
                  sx={{
                    borderRadius: "18px",
                    background: "#fff",
                    border: "1px solid #edf0f2",
                    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
                    cursor: "pointer",
                    height: 178,
                    overflow: "hidden",
                    position: "relative",
                    animation: `fadeUp .45s ease ${delay} both`,
                    transition: "all .22s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 10px 28px rgba(16,24,40,.08)",
                      borderColor: "rgba(12,138,155,.28)",
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 2.2,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* top */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 1.6,
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "11px",
                          background: TEAL_LIGHT,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionOutlinedIcon
                          sx={{ color: TEAL, fontSize: 19 }}
                        />
                      </Box>

                      <Box
                        sx={{ display: "flex", gap: 0.3 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/ats/sop/view/${sop.id}`)}
                            sx={{
                              color: "#adb5bd",
                              "&:hover": { color: TEAL, bgcolor: TEAL_LIGHT },
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/ats/sop/edit/${sop.id}`)}
                            sx={{
                              color: "#adb5bd",
                              "&:hover": {
                                color: "#3b5bdb",
                                bgcolor: "#edf2ff",
                              },
                            }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(sop.id)}
                            sx={{
                              color: "#adb5bd",
                              "&:hover": {
                                color: "#e03131",
                                bgcolor: "#fff0f0",
                              },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* title */}
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: "0.92rem",
                        color: "#111827",
                        lineHeight: 1.25,
                        mb: 0.5,
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {sop.title}
                    </Typography>

                    {/* description */}
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: "0.76rem",
                        color: "#6b7280",
                        fontWeight: 500,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.35,
                      }}
                    >
                      {sop.description || "No description provided."}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    {/* status */}
                    <Chip
                      label={sop.status}
                      size="small"
                      sx={{
                        alignSelf: "flex-start",
                        bgcolor: st.bg,
                        color: st.color,
                        fontFamily: FONT,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        height: 22,
                      }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default SopFolderDetails;
